/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * "Dicci la tua" (migrazione 016): due cose diverse, tenute separate apposta.
 *
 * 1. Il QUESTIONARIO: ti piace l'app, quale funzione vorresti, cosa
 *    miglioreresti. E' un'opinione, non una richiesta: nessuno stato da seguire.
 * 2. Un PROBLEMA CON L'APP: qualcosa che non funziona. Questa e' una richiesta
 *    vera, con uno stato e una risposta dell'assistenza.
 *
 * Il rischio da evitare e' che un cliente scriva qui "la caldaia perde": per
 * lui "problema" vuol dire l'impianto. Il riquadro lo dice prima del modulo e lo
 * manda alla richiesta d'intervento, che e' il canale che fa uscire un tecnico.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, Send, Wrench, ChevronRight, X, Heart } from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { feedbackAPI, problemiAPI } from '../api/feedback';

const ACCENT = '#3B82F6';

const STATI = {
  nuova:           { label: 'Inviata',         color: '#374151', bg: '#F3F4F6' },
  presa_in_carico: { label: 'Presa in carico', color: '#6D28D9', bg: '#EDE9FE' },
  in_corso:        { label: 'In lavorazione',  color: '#B45309', bg: '#FEF3C7' },
  completata:      { label: 'Risolta',         color: '#065F46', bg: '#D1FAE5' },
  rifiutata:       { label: 'Chiusa',          color: '#991B1B', bg: '#FEE2E2' },
};

const VOTI = ['', 'Per niente', 'Poco', 'Così così', 'Mi piace', 'Mi piace molto'];

const errMsg = (e, def) => e?.response?.data?.error || def;
const fmtData = (d) => new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

export default function DicciLaTua() {
  const [aperto, setAperto] = useState(null);
  return (
    <ClienteLayout title="Dicci la tua">
      <Questionario />
      <Problemi onApri={setAperto} />
      {aperto && <DettaglioProblema id={aperto} onChiudi={() => setAperto(null)} />}
    </ClienteLayout>
  );
}

function Questionario() {
  const queryClient = useQueryClient();
  const [voto, setVoto] = useState(0);
  const [sopra, setSopra] = useState(0);
  const [nuovaFunzione, setNuovaFunzione] = useState('');
  const [daMigliorare, setDaMigliorare] = useState('');
  const [errore, setErrore] = useState('');
  const [grazie, setGrazie] = useState(false);

  const { data } = useQuery({ queryKey: ['feedback-mio'], queryFn: feedbackAPI.mio });
  const invia = useMutation({
    mutationFn: () => feedbackAPI.invia({ voto, nuova_funzione: nuovaFunzione, da_migliorare: daMigliorare }),
    onSuccess: () => { setGrazie(true); queryClient.invalidateQueries({ queryKey: ['feedback-mio'] }); },
    onError: (e) => setErrore(errMsg(e, 'Invio non riuscito')),
  });

  const ultimo = data?.ultimo;
  if (grazie || ultimo?.recente) {
    return (
      <div style={s.card}>
        <div style={s.grazie}>
          <Heart size={28} color="#EC4899" fill="#FCE7F3" />
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Grazie!</div>
            <div style={s.testoPiccolo}>
              Abbiamo ricevuto la tua opinione. La leggiamo tutta e ci aiuta a decidere cosa migliorare.
              Puoi lasciarne un'altra da domani.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mostrato = sopra || voto;
  return (
    <div style={s.card}>
      <h2 style={s.titolo}>Ti piace l'app?</h2>
      {ultimo && <div style={s.testoPiccolo}>L'ultima volta ci hai dato {ultimo.voto} su 5, il {fmtData(ultimo.created_at)}.</div>}
      <div style={s.stelle} onMouseLeave={() => setSopra(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" style={s.stellaBtn} onClick={() => setVoto(n)} onMouseEnter={() => setSopra(n)}
            aria-label={`${n} su 5: ${VOTI[n]}`}>
            <Star size={38} color={n <= mostrato ? '#F59E0B' : '#D1D5DB'} fill={n <= mostrato ? '#F59E0B' : 'none'} />
          </button>
        ))}
      </div>
      <div style={s.votoTesto}>{mostrato ? VOTI[mostrato] : 'Tocca una stella'}</div>

      <label style={s.label}>Quale nuova funzione vorresti?</label>
      <textarea style={s.input} rows={3} value={nuovaFunzione} maxLength={3000} onChange={(e) => setNuovaFunzione(e.target.value)}
        placeholder="es. ricevere un promemoria prima della manutenzione" />

      <label style={s.label}>Cosa vorresti migliorare?</label>
      <textarea style={s.input} rows={3} value={daMigliorare} maxLength={3000} onChange={(e) => setDaMigliorare(e.target.value)}
        placeholder="Quello che trovi scomodo o poco chiaro" />

      {errore && <div style={s.errore}>{errore}</div>}
      <button style={{ ...s.btnPrimario, opacity: voto ? 1 : 0.55 }} disabled={!voto || invia.isPending} onClick={() => { setErrore(''); invia.mutate(); }}>
        <Send size={17} /> {invia.isPending ? 'Invio...' : 'Invia la tua opinione'}
      </button>
      <div style={s.testoPiccolo}>Le risposte le legge solo il nostro ufficio, per migliorare l'app.</div>
    </div>
  );
}

function Problemi({ onApri }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [apriModulo, setApriModulo] = useState(false);
  const [titolo, setTitolo] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [errore, setErrore] = useState('');

  const { data } = useQuery({ queryKey: ['problemi-app'], queryFn: problemiAPI.list });
  const crea = useMutation({
    mutationFn: () => problemiAPI.crea(titolo, descrizione),
    onSuccess: (x) => {
      setTitolo(''); setDescrizione(''); setApriModulo(false);
      queryClient.invalidateQueries({ queryKey: ['problemi-app'] });
      onApri(x.id);
    },
    onError: (e) => setErrore(errMsg(e, 'Invio non riuscito')),
  });

  const elenco = data?.segnalazioni || [];

  return (
    <div style={s.card}>
      <h2 style={s.titolo}>Qualcosa nell'app non funziona?</h2>

      <div style={s.avvisoImpianto}>
        <Wrench size={20} style={{ flexShrink: 0 }} />
        <div>
          <strong>Un guasto all'impianto non va scritto qui.</strong> Per la caldaia, il climatizzatore
          o qualunque apparecchio usa la richiesta d'intervento: è così che ti mandiamo un tecnico.
          <button style={s.linkBtn} onClick={() => navigate('/nuovo')}>Richiedi un intervento →</button>
        </div>
      </div>

      {!apriModulo ? (
        <button style={s.btnSecondario} onClick={() => setApriModulo(true)}>Segnala un problema con l'app</button>
      ) : (
        <>
          <label style={s.label}>Cosa non funziona?</label>
          <input style={s.input} value={titolo} maxLength={200} onChange={(e) => setTitolo(e.target.value)}
            placeholder="es. Non riesco ad aprire le foto dell'intervento" />
          <label style={s.label}>Racconta cosa stavi facendo</label>
          <textarea style={s.input} rows={4} value={descrizione} onChange={(e) => setDescrizione(e.target.value)}
            placeholder="Da telefono o da computer, in quale pagina, cosa è comparso" />
          {errore && <div style={s.errore}>{errore}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <button style={s.btnSecondario} onClick={() => setApriModulo(false)}>Annulla</button>
            <button style={{ ...s.btnPrimario, marginTop: 0, opacity: titolo.trim() ? 1 : 0.55 }}
              disabled={!titolo.trim() || crea.isPending} onClick={() => { setErrore(''); crea.mutate(); }}>
              <Send size={16} /> {crea.isPending ? 'Invio...' : 'Invia'}
            </button>
          </div>
        </>
      )}

      {elenco.length > 0 && (
        <>
          <h3 style={s.sottotitolo}>Le tue segnalazioni</h3>
          <div style={s.elenco}>
            {elenco.map((x) => (
              <button key={x.id} style={s.riga} onClick={() => onApri(x.id)}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={s.rigaTitolo}>{x.titolo}</span>
                  <span style={s.testoPiccolo}>{fmtData(x.created_at)}</span>
                </span>
                <span style={{ ...s.badge, backgroundColor: STATI[x.stato].bg, color: STATI[x.stato].color }}>{STATI[x.stato].label}</span>
                <ChevronRight size={18} color="#9CA3AF" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DettaglioProblema({ id, onChiudi }) {
  const queryClient = useQueryClient();
  const [commento, setCommento] = useState('');
  const [errore, setErrore] = useState('');
  const { data: x, isLoading } = useQuery({ queryKey: ['problema', id], queryFn: () => problemiAPI.detail(id) });
  const commenta = useMutation({
    mutationFn: () => problemiAPI.commenta(id, commento),
    onSuccess: () => {
      setCommento('');
      queryClient.invalidateQueries({ queryKey: ['problema', id] });
      queryClient.invalidateQueries({ queryKey: ['problemi-app'] });
    },
    onError: (e) => setErrore(errMsg(e, 'Invio non riuscito')),
  });

  return (
    <div style={s.overlay} onClick={onChiudi}>
      <div style={s.modale} onClick={(e) => e.stopPropagation()}>
        <div style={s.modaleTesta}>
          <h3 style={{ margin: 0, fontSize: '1.02rem' }}>{x ? x.titolo : 'Segnalazione'}</h3>
          <button style={s.iconaBtn} onClick={onChiudi} aria-label="Chiudi"><X size={20} /></button>
        </div>
        <div style={s.modaleCorpo}>
          {isLoading || !x ? <div style={s.testoPiccolo}>Caricamento...</div> : (
            <>
              <span style={{ ...s.badge, backgroundColor: STATI[x.stato].bg, color: STATI[x.stato].color }}>{STATI[x.stato].label}</span>
              {x.risposta && <div style={s.risposta}><strong>Risposta dell'assistenza:</strong> {x.risposta}</div>}
              {x.descrizione && <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: '0.93rem' }}>{x.descrizione}</p>}
              <h4 style={s.sottotitolo}>Conversazione</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {x.storico.map((h) => (
                  <div key={h.id} style={{ ...s.messaggio, ...(h.mio ? s.messaggioMio : {}) }}>
                    <div style={s.testoPiccolo}>{h.mio ? 'Tu' : h.utente} · {fmtData(h.created_at)}</div>
                    {h.stato_nuovo && <div style={{ fontSize: '0.85rem' }}>{h.stato_precedente ? `Stato: ${STATI[h.stato_nuovo]?.label}` : 'Inviata'}</div>}
                    {h.commento && <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.92rem', marginTop: '0.15rem' }}>{h.commento}</div>}
                  </div>
                ))}
              </div>
              {errore && <div style={s.errore}>{errore}</div>}
            </>
          )}
        </div>
        {x && (
          <div style={s.modalePiede}>
            <input style={{ ...s.input, flex: 1 }} value={commento} onChange={(e) => setCommento(e.target.value)} placeholder="Scrivi all'assistenza..." />
            <button style={s.btnInvia} disabled={!commento.trim() || commenta.isPending} onClick={() => commenta.mutate()} aria-label="Invia">
              <Send size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  card: { backgroundColor: 'white', borderRadius: '14px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '1.25rem' },
  titolo: { margin: '0 0 0.4rem', fontSize: '1.15rem', fontWeight: 700 },
  sottotitolo: { fontSize: '0.92rem', fontWeight: 700, margin: '1.25rem 0 0.5rem' },
  testoPiccolo: { display: 'block', fontSize: '0.8rem', color: '#6B7280', marginTop: '0.35rem', lineHeight: 1.5 },
  stelle: { display: 'flex', gap: '0.25rem', marginTop: '0.75rem' },
  stellaBtn: { background: 'none', border: 'none', padding: '0.15rem', cursor: 'pointer', lineHeight: 0 },
  votoTesto: { fontSize: '0.88rem', color: '#4B5563', marginTop: '0.25rem', minHeight: '1.2rem', fontWeight: 600 },
  label: { display: 'block', fontSize: '0.86rem', fontWeight: 600, color: '#374151', margin: '1rem 0 0.35rem' },
  input: { width: '100%', padding: '0.7rem 0.8rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.95rem', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white', resize: 'vertical' },
  btnPrimario: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem', marginTop: '1rem', padding: '0.75rem 1.2rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', minHeight: '46px' },
  btnSecondario: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.7rem 1.1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer', minHeight: '44px' },
  linkBtn: { display: 'block', marginTop: '0.45rem', padding: 0, background: 'none', border: 'none', color: '#B45309', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', textAlign: 'left' },
  avvisoImpianto: { display: 'flex', gap: '0.65rem', padding: '0.8rem 0.9rem', backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', color: '#78350F', borderRadius: '10px', fontSize: '0.87rem', lineHeight: 1.5, margin: '0.6rem 0 1rem' },
  grazie: { display: 'flex', gap: '0.85rem', alignItems: 'flex-start' },
  errore: { marginTop: '0.75rem', padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.86rem' },
  elenco: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  riga: { display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.75rem', backgroundColor: '#F9FAFB', border: 'none', borderRadius: '10px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' },
  rigaTitolo: { display: 'block', fontWeight: 600, fontSize: '0.92rem', color: '#111827' },
  badge: { display: 'inline-block', padding: '0.18rem 0.55rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 },
  modale: { backgroundColor: 'white', borderRadius: '14px', width: '100%', maxWidth: '540px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  modaleTesta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.9rem 1.1rem', borderBottom: '1px solid #E5E7EB' },
  modaleCorpo: { padding: '1rem 1.1rem', overflowY: 'auto', flex: 1 },
  modalePiede: { display: 'flex', gap: '0.5rem', padding: '0.75rem 1.1rem', borderTop: '1px solid #E5E7EB' },
  iconaBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', display: 'inline-flex', padding: '0.3rem' },
  risposta: { marginTop: '0.8rem', padding: '0.65rem 0.8rem', borderLeft: `4px solid ${ACCENT}`, backgroundColor: '#EFF6FF', borderRadius: '6px', fontSize: '0.9rem', lineHeight: 1.5 },
  messaggio: { backgroundColor: '#F3F4F6', borderRadius: '12px', padding: '0.55rem 0.75rem', maxWidth: '88%' },
  messaggioMio: { alignSelf: 'flex-end', backgroundColor: '#DBEAFE' },
  btnInvia: { width: '46px', height: '46px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer' },
};
